/**
 * FieldInspector — the per-field popover the canvas opens on selection. Edits
 * label/value/alignment via the shared fieldEditing helpers; the VALUE of a
 * LIVE field (key conventional for this style+group — conventionalLiveKeys,
 * NOT the loaded doc, so a saved custom field can never lock) is disabled
 * with an explainer, matching the mint-refresh contract. Formatter plumbing
 * (dateStyle/currencyCode/…) is deliberately NOT here — hidden plumbing per
 * the spec; the renderer already drops incompatible formatters.
 */
import type { PassDesign } from '@insurwiz/wallet-core';

import { useT } from '../../i18n';
import {
  moveField,
  removeField,
  setFieldLabelSlugged,
  setFieldProp,
  type FieldGroup,
} from '../../lib/fieldEditing';
import { HelpText } from '../designer/HelpHint';

import { Popover } from './Popover';
import { conventionalLiveKeyUnion, conventionalLiveKeys } from './liveFieldPalette';

export interface FieldInspectorProps {
  design: PassDesign;
  group: FieldGroup;
  index: number;
  onChange: (next: PassDesign) => void;
  onClose: () => void;
  /**
   * moveField swaps two adjacent rows, so the field this popover is
   * inspecting now lives at index±1 after a successful move — without this,
   * the host's `selected.index` goes stale and the popover silently starts
   * editing whichever field swapped INTO the old slot. Called ONLY when the
   * move actually happened (not on a boundary no-op).
   */
  onIndexChange?: (newIndex: number) => void;
}

export function FieldInspector({
  design,
  group,
  index,
  onChange,
  onClose,
  onIndexChange,
}: FieldInspectorProps): JSX.Element | null {
  const t = useT();
  const field = (design.fields[group] ?? [])[index];
  if (field === undefined) return null;
  const liveKeys = conventionalLiveKeys(design.style)[group];
  const live = liveKeys.has(field.key);

  // moveField returns the SAME design reference at a group boundary (its own
  // no-op contract — see lib/fieldEditing.ts) — use that to skip both the
  // design update and the index-follow callback for a no-op click.
  function handleMove(delta: -1 | 1): void {
    const moved = moveField(design, group, index, delta);
    if (moved !== design) {
      onChange(moved);
      onIndexChange?.(index + delta);
    }
  }

  return (
    <Popover label={t('inspector.title')} onClose={onClose}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">{t('inspector.field')}</span>
          {live ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-800 bg-amber-50 rounded-full px-2 py-0.5">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> {t('inspector.liveBadge')}
            </span>
          ) : null}
        </div>

        <label className="block text-xs text-slate-600">
          {t('inspector.label')}
          <input
            aria-label={t('inspector.label')}
            className="input mt-1 w-full"
            value={field.label ?? ''}
            // Shared slug-on-first-label-edit rule (the panel's): a NEW custom
            // row ({key:''}) gets its key slugged from the label — the canvas
            // exposes no key input, so this is the ONLY way it turns valid.
            // Reserved pool = ALL groups' conventional keys (the engine
            // patches by key across every group, so 'Gate' in secondary must
            // dedupe to gate-2, never collide into the live bare 'gate').
            onChange={(e) =>
              onChange(
                setFieldLabelSlugged(
                  design,
                  group,
                  index,
                  e.target.value,
                  conventionalLiveKeyUnion(design.style),
                ),
              )
            }
          />
        </label>

        <label className="block text-xs text-slate-600">
          {t('inspector.value')}
          <input
            aria-label={t('inspector.value')}
            className="input mt-1 w-full disabled:bg-slate-100"
            value={field.value}
            disabled={live}
            onChange={(e) => onChange(setFieldProp(design, group, index, 'value', e.target.value))}
          />
        </label>
        {live ? (
          <p className="text-[11px] text-slate-500">
            {/* Flight copy only where a flight exists: on the generic canvas the
                live values come from the generation-time data, never a flight. */}
            {t(
              design.style === 'boardingPass' ? 'inspector.liveHint' : 'inspector.liveHintGeneric',
            )}
          </p>
        ) : null}

        <label className="block text-xs text-slate-600">
          {t('inspector.alignment')}
          <select
            aria-label={t('inspector.alignment')}
            className="input mt-1 w-full"
            value={field.textAlignment ?? ''}
            onChange={(e) =>
              onChange(
                setFieldProp(design, group, index, 'textAlignment', e.target.value || undefined),
              )
            }
          >
            <option value="">{t('inspector.alignAuto')}</option>
            <option value="PKTextAlignmentLeft">{t('inspector.alignLeft')}</option>
            <option value="PKTextAlignmentCenter">{t('inspector.alignCenter')}</option>
            <option value="PKTextAlignmentRight">{t('inspector.alignRight')}</option>
          </select>
        </label>
        <HelpText>{t('inspector.alignOsHint')}</HelpText>

        <div className="flex items-center justify-between pt-1">
          <div className="flex gap-1">
            <button
              type="button"
              aria-label={t('inspector.moveLeft')}
              className="btn-secondary px-2 py-1 text-xs"
              onClick={() => handleMove(-1)}
            >
              ←
            </button>
            <button
              type="button"
              aria-label={t('inspector.moveRight')}
              className="btn-secondary px-2 py-1 text-xs"
              onClick={() => handleMove(1)}
            >
              →
            </button>
          </div>
          <button
            type="button"
            className="text-xs text-red-600 hover:underline"
            onClick={() => {
              onChange(removeField(design, group, index));
              onClose();
            }}
          >
            {t('inspector.deleteField')}
          </button>
        </div>
      </div>
    </Popover>
  );
}
